import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getProfilePlaceholder } from '../../utils/profilePlaceholders';

function decodeEntities(encodedString) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = encodedString;
  return textarea.value;
}

function PublicProfile() {
  const { slug } = useParams();
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPublicProfileData = async () => {
      try {
        const response = await axios.get(`/api/public/profile/${slug}`);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching public profile:', error);
      }
    };

    if (slug) {
      fetchPublicProfileData();
    }
  }, [slug]);

  if (!user) return <div className="p-4 text-center text-gray-500">Loading public profile...</div>;

  const { addressLine1: displayAddressLine1 } = getProfilePlaceholder(user, {
    b_address: null,
    b_city: null,
    b_state: null,
    b_zip_code: null,
  });

  const { addressLine2: displayAddressLine2 } = getProfilePlaceholder(user, {
    b_address: null,
    b_city: null,
    b_state: null,
    b_zip_code: null,
  });

  const handleCompanyClick = () => {
    const companySlug = (user?.b_website || '')
      .replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
    if (companySlug) {
      navigate(`/company/public/${companySlug}`);
    }
  };

  const formatWebsite = (website) => website?.endsWith('/') ? website.slice(0, -1) : website;

  return (
    <div className="container mx-auto w-4/5 pt-6 flex flex-col min-h-screen">
      <div className="flex flex-col md:flex-row gap-3 flex-grow">

        {/* Left Column - User Info */}
        <div className="w-full md:w-[24%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-fit">
          <div className="mb-6">
            <div className="relative w-32 mx-auto">
              <img
                src={user.u_profile_picture || '/images/default-avatar.png'}
                alt="User Profile"
                className="w-full h-full rounded-full object-cover"
              />
              {user.u_customer_focus && (
                <div className="absolute -bottom-2 -right-6 group">
                  <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                    {user.u_customer_focus}
                  </div>
                  <div className="hidden group-hover:block absolute z-20 bg-black text-white text-xs p-2 rounded-md w-80 -translate-x-1/2 left-1/2 mt-6">
                    This user primarily serves {user.u_customer_focus} clients.
                  </div>
                </div>
              )}
            </div>

            <h2 className="text-xl font-semibold text-center mb-1 mt-4">{user.u_first_name} {user.u_last_name}</h2>
            <p className="text-base text-gray-700 text-center mb-2">{user.u_job_title}</p>
            <p className="text-sm text-gray-600 text-center mb-1">
              <a href={`tel:${user.u_phone_number}`} className="hover:text-blue-600">{user.u_phone_number}</a>
            </p>
            <p className="text-sm leading-tight text-gray-600 text-center mb-3">
              <a href={`mailto:${user.u_email}`} className="hover:text-blue-600">{user.u_email}</a>
            </p>

            <div className="mt-6 text-center">
              <div className="flex justify-center text-yellow-400">★★★★★</div>
              <p className="text-lg font-bold mt-1">9.4</p>
            </div>
          </div>
        </div>

        {/* Middle Column - Business Info */}
        <div className="w-full md:w-[48%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
          <div className="w-full mb-0">
            <h1
              className="text-2xl font-bold break-words text-[#00aef7] leading-tight cursor-pointer"
              onClick={handleCompanyClick}
            >
              {user.b_name}
            </h1>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex-1 pt-1.5">
              {user?.b_show_address !== false && (
                <div style={{ minHeight: '60px' }}>
                  <p className="text-sm text-gray-600 leading-none">
                    {user?.b_address?.trim() ? user.b_address : displayAddressLine1}
                  </p>
                  <p className="text-sm text-gray-600 leading-normal mb-3">
                    {(user?.b_city || user?.b_state || user?.b_zip_code)
                      ? `${user.b_city || ''}, ${user.b_state || ''} ${user.b_zip_code || ''}`
                      : displayAddressLine2}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-600 leading-none">
                <a href={`tel:${user.b_phone}`} className="hover:text-blue-600">{user.b_phone}</a>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <a href={user.b_website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">{formatWebsite(user.b_website)}</a>
              </p>
            </div>

            <div className="w-[54%] relative -mt-6">
              <div className="relative w-full h-12 group">
                {user.b_logo ? (
                  <div className="cursor-pointer" onClick={handleCompanyClick}>
                    <img
                      src={`${process.env.REACT_APP_API_URL}${user.b_logo}`}
                      alt="Business Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <img
                    src="/images/Logo_Placeholder.jpg"
                    alt="Logo Placeholder"
                    className="w-full h-full object-contain opacity-50 border-2 border-[#00aef7]/30 rounded-md"
                  />
                )}

                {user.b_customer_focus && (
                  <div className="absolute -top-0.5 -right-1 z-20">
                    <div className="relative group">
                      <div className="text-sm font-bold bg-[#00aef7]/10 px-2 py-0.5 rounded-full border border-[#00aef7]/20 text-[#00aef7] cursor-help">
                        {user.b_customer_focus}
                      </div>
                      <div className="hidden group-hover:block absolute z-20 bg-black text-white text-xs p-2 rounded-md w-80 -translate-x-1/2 left-1/2 mt-6">
                        This business primarily serves {user.b_customer_focus} clients.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text-center w-[54%] self-end -mt-8">
            <p className="text-lg font-bold text-gray-600">{user.b_business_industry}</p>
          </div>

          <div className="mt-3 flex-1 overflow-y-auto max-h-[300px] pr-2 relative">
            {user?.b_about_us && (() => {
              const raw = user.b_about_us;
              const decoded = decodeEntities(raw);
              const cleaned = DOMPurify.sanitize(decoded);
              return (
                <div
                  className="w-full h-full p-2 pt-8 text-gray-700 bg-white ql-editor"
                  dangerouslySetInnerHTML={{ __html: cleaned }}
                />
              );
            })()}
          </div>
        </div>

        {/* Right Column - Reviews & Ratings */}
        <div className="w-full md:w-[28%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)]">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">Reviews & Ratings</h2>
          </div>
        </div>

      </div>
    </div>
  );
}

export default PublicProfile;
